import type {
  AuctionHouseItem,
  PlayerData,
  ItemSpawnData,
  EffectSpawnData,
  CreativeLogData,
  ShopData,
  EconomyLogData,
  WarpData,
  PlayerStreamLogData,
} from '../types/player';
import type { NetworkData } from '../types/networkData';

/**
 * Game Redis reads — through argus-backend, not Webdis.
 *
 * `GET https://spy.playcdu.co/api/notes/staff/redis/*` (see argus-backend
 * docs/staff/GAME_REDIS_API_DOCS.md). The backend reaches the private Redis over a Workers VPC
 * tunnel and can only issue a fixed set of read commands against keys it builds from a validated
 * UUID or cluster id; there is no endpoint that takes a command, a key or a pattern, and no write
 * path from the browser. Auth is the Cloudflare Access session cookie, like every other
 * `/api/notes/*` call. The service keeps the method names and `{ data, key }` shapes callers
 * already use.
 */

const GAME_REDIS_API_BASE_URL = 'https://spy.playcdu.co/api/notes/staff/redis';

interface DocumentResponse<T> {
  key: string;
  data: T | null;
}

interface StreamResponse<TEntry> {
  key: string;
  entries: TEntry[];
  truncated: boolean;
}

export type ShopKind = 'AH' | 'AS' | 'CS';

export interface PlayerSessions {
  uuid: string;
  clusters: { cluster: string; key: string; entries: PlayerStreamLogData['XRANGE']; truncated: boolean; error?: string }[];
}

export interface PlayerListings {
  uuid: string;
  clusters: string[];
  listings: { cluster: string; kind: ShopKind; key: string; item: AuctionHouseItem }[];
  errors: { cluster: string; kind: ShopKind; key: string; error: string }[];
}

export class GameRedisApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'GameRedisApiError';
    this.status = status;
  }
}

/**
 * "The key is absent" — an answer, not a failure. `definitive` tells the stale-while-revalidate
 * cache (services/swrCache.ts) to evict rather than keep showing an older copy.
 */
export class GameRedisNotFoundError extends GameRedisApiError {
  readonly definitive = true;

  constructor(message: string) {
    super(404, message);
    this.name = 'GameRedisNotFoundError';
  }
}

export class GameRedisService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = GAME_REDIS_API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string): Promise<T> {
    // 'no-cache': the browser may keep a copy but revalidates it on every call with the ETag the
    // backend sets, so an unchanged document costs a 304 and no transfer. Never served stale.
    const response = await fetch(`${this.baseUrl}${path}`, { method: 'GET', cache: 'no-cache' });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new GameRedisApiError(response.status, body?.error || `Game Redis API error: ${response.status}`);
    }
    return response.json();
  }

  private document<T>(path: string): Promise<DocumentResponse<T>> {
    return this.request<DocumentResponse<T>>(path);
  }

  private async stream<TEntry>(path: string): Promise<{ data: { XRANGE: TEntry[] } | null; key: string }> {
    const { key, entries } = await this.request<StreamResponse<TEntry>>(path);
    if (!entries || entries.length === 0) return { data: null, key };
    return { data: { XRANGE: entries }, key };
  }

  /** The network profile. Throws when the key is absent (callers treat that as "not found"). */
  async getPlayerData(playerId: string): Promise<{ data: PlayerData; key: string }> {
    const { key, data } = await this.document<PlayerData>(`/player/${encodeURIComponent(playerId)}`);
    if (!data) throw new GameRedisNotFoundError(`Player data not found for ${playerId} (JSON.GET returned null)`);
    return { data, key };
  }

  getItemSpawnData(playerId: string): Promise<{ data: ItemSpawnData | null; key: string }> {
    return this.document<ItemSpawnData>(`/player/${encodeURIComponent(playerId)}/item-spawns`);
  }

  getEffectSpawnData(playerId: string): Promise<{ data: EffectSpawnData | null; key: string }> {
    return this.document<EffectSpawnData>(`/player/${encodeURIComponent(playerId)}/effect-spawns`);
  }

  getCreativeLog(playerId: string): Promise<{ data: CreativeLogData | null; key: string }> {
    return this.stream<CreativeLogData['XRANGE'][number]>(`/player/${encodeURIComponent(playerId)}/creative-log`);
  }

  getPlayerEconomyLog(uuid: string): Promise<{ data: EconomyLogData | null; key: string }> {
    return this.stream<EconomyLogData['XRANGE'][number]>(`/player/${encodeURIComponent(uuid)}/economy-log`);
  }

  /** Cluster ids from the serverData search index. */
  async getClusterIds(): Promise<string[]> {
    const { clusters } = await this.request<{ clusters: string[] }>('/clusters');
    return [...clusters].sort();
  }

  getEconomyLog(clusterId: string): Promise<{ data: EconomyLogData | null; key: string }> {
    return this.stream<EconomyLogData['XRANGE'][number]>(`/cluster/${encodeURIComponent(clusterId)}/economy-log`);
  }

  private shop(clusterId: string, kind: 'AH' | 'AS' | 'CS'): Promise<{ data: ShopData | null; key: string }> {
    return this.document<ShopData>(`/cluster/${encodeURIComponent(clusterId)}/shop/${kind}`);
  }

  getAuctionHouseData(clusterId: string): Promise<{ data: ShopData | null; key: string }> {
    return this.shop(clusterId, 'AH');
  }

  getAdminShopData(clusterId: string): Promise<{ data: ShopData | null; key: string }> {
    return this.shop(clusterId, 'AS');
  }

  getChestShopData(clusterId: string): Promise<{ data: ShopData | null; key: string }> {
    return this.shop(clusterId, 'CS');
  }

  /** A cluster's session stream; with `uuid`, only that player's entries (filtered in the backend, far less to transfer). */
  getPlayerStreamLog(clusterId: string, options: { uuid?: string } = {}): Promise<{ data: PlayerStreamLogData | null; key: string }> {
    const query = options.uuid ? `?uuid=${encodeURIComponent(options.uuid)}` : '';
    return this.stream<PlayerStreamLogData['XRANGE'][number]>(`/cluster/${encodeURIComponent(clusterId)}/player-stream${query}`);
  }

  /**
   * One player's session entries from every cluster's stream. The backend pages every stream side
   * by side on one connection and returns only that player's entries.
   */
  getPlayerSessions(uuid: string): Promise<PlayerSessions> {
    return this.request<PlayerSessions>(`/player/${encodeURIComponent(uuid)}/sessions`);
  }

  /**
   * Every shop listing on every cluster where the player is the seller or the buyer. The backend
   * reads every cluster's three shop documents in pipelined batches on one connection.
   */
  getPlayerListings(uuid: string): Promise<PlayerListings> {
    return this.request<PlayerListings>(`/player/${encodeURIComponent(uuid)}/listings`);
  }

  getWarpData(): Promise<{ data: WarpData | null; key: string }> {
    return this.document<WarpData>('/warps');
  }

  getNetworkData(): Promise<{ data: NetworkData | null; key: string }> {
    return this.document<NetworkData>('/network');
  }
}

export const gameRedisService = new GameRedisService();
